import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Bookmark, ExternalLink, DollarSign, Clock, Building2,
  Tag, FileText, Sparkles, Loader2, CheckCircle2
} from "lucide-react";
import AIFitAnalysis from "@/components/AIFitAnalysis";

const fmtMoney = (n) => n == null ? null : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function GrantDetail() {
  const { id } = useParams();
  const [grant, setGrant] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [creating, setCreating] = useState(false);
  const [existingApp, setExistingApp] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/grants/${id}`).then((r) => setGrant(r.data)).catch(() => toast.error("Grant not found"));
    api.get("/grants/saved/list").then((r) => {
      setIsSaved((r.data.grants || []).some((g) => g.id === id));
    });
    api.get("/applications").then((r) => {
      const found = (r.data.applications || []).find((a) => a.grant_id === id);
      if (found) setExistingApp(found);
    });
    // Only `id` is a reactive dependency; api/toast/setters are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Filter+map on every render is fine for typical eligibility lists (<20 items),
  // but memoize to satisfy the linter and avoid recomputing on unrelated re-renders.
  // NOTE: Must be declared before any early return to preserve hook call order.
  const eligibilityItems = useMemo(
    () => (grant?.eligibility || []).filter(Boolean),
    [grant?.eligibility],
  );

  if (!grant) {
    return <div className="py-24 text-center text-[#5E5E5E]"><Loader2 className="w-5 h-5 animate-spin inline"/></div>;
  }

  const toggleSave = async () => {
    if (isSaved) {
      await api.delete(`/grants/${id}/save`);
      setIsSaved(false);
      toast.success("Removed from saved");
    } else {
      await api.post(`/grants/${id}/save`);
      setIsSaved(true);
      toast.success("Saved to your list");
    }
  };

  const createApp = async () => {
    // Guard: user must have started trial or subscribed to apply.
    setCreating(true);
    try {
      const res = await api.post(`/applications/apply/${id}`);
      const { application, external_url } = res.data;
      setExistingApp(application);
      // Open the provider's application in a new tab (spec point 2).
      if (external_url) {
        window.open(external_url, "_blank", "noopener,noreferrer");
      }
      // Spec point 7 — exact confirmation copy.
      toast.success(
        "Your application has been added to your Grant Pipeline. Complete the application in the new tab. Return here to monitor and manage your application status.",
        { duration: 8000 }
      );
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        toast.error(err.response.data?.detail || "Subscription required.", { duration: 6000 });
        navigate("/pricing");
        return;
      }
      toast.error(err?.response?.data?.detail || "Could not start application");
    } finally {
      setCreating(false);
    }
  };

  const deadlineDate = grant.deadline ? new Date(grant.deadline) : null;
  const days = deadlineDate ? Math.ceil((deadlineDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="space-y-8 max-w-4xl" data-testid="grant-detail">
      <Link to="/grants" className="text-sm text-[#5E5E5E] hover:text-[#1C3F35] inline-flex items-center gap-1" data-testid="grant-back">
        <ArrowLeft className="w-4 h-4"/> Back to grants
      </Link>

      <div>
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#E8ECEB] text-[#1C3F35]" data-testid="grant-category">
            {grant.category}
          </span>
          {grant.state && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#C1654B]/10 text-[#C1654B]" data-testid="grant-state-badge">
              {grant.state}{grant.county ? ` · ${grant.county} County` : ""}
            </span>
          )}
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#F9F8F6] text-[#5E5E5E]">
            {grant.source}
          </span>
        </div>
        <h1 className="font-heading font-light text-4xl md:text-5xl tracking-tighter leading-tight text-[#1A1A1A]" data-testid="grant-title">
          {grant.title}
        </h1>
        <div className="flex items-center gap-2 mt-4 text-[#5E5E5E]">
          <Building2 className="w-4 h-4"/><span data-testid="grant-agency">{grant.agency}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {existingApp ? (
          <Button
            onClick={() => navigate("/applications")}
            data-testid="grant-view-application"
            className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
          >
            <CheckCircle2 className="w-4 h-4 mr-2"/>View your application
          </Button>
        ) : (
          <Button
            onClick={createApp}
            disabled={creating || !grant.url}
            data-testid="grant-apply"
            className="bg-[#1C3F35] text-white hover:bg-[#1A362D]"
            title={grant.url ? "" : "This grant is missing an application URL."}
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2"/> : <ExternalLink className="w-4 h-4 mr-2"/>}
            Apply for Grant
          </Button>
        )}
        <Button
          onClick={toggleSave}
          variant="outline"
          data-testid="grant-save"
          className="rounded-md border-[#E6E4DF]"
        >
          <Bookmark className="w-4 h-4 mr-2" fill={isSaved ? "currentColor" : "none"}/>
          {isSaved ? "Saved" : "Save"}
        </Button>
        <Button
          onClick={() => navigate(`/ai-writer?grant=${id}`)}
          variant="outline"
          data-testid="grant-ai-write"
          className="rounded-md border-[#E6E4DF]"
        >
          <Sparkles className="w-4 h-4 mr-2"/>Draft with AI
        </Button>
        {grant.url && (
          <a href={grant.url} target="_blank" rel="noopener noreferrer" data-testid="grant-source-link"
             className="inline-flex items-center gap-1 text-sm text-[#5E5E5E] hover:text-[#1C3F35] px-3 py-2">
            View on source <ExternalLink className="w-3 h-3"/>
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="grant-funding">
          <div className="label-eyebrow mb-2">Funding range</div>
          <div className="font-heading text-2xl font-medium text-[#1A1A1A]">
            {fmtMoney(grant.funding_min) || "—"} <span className="text-[#5E5E5E] text-lg">–</span> {fmtMoney(grant.funding_max) || "—"}
          </div>
        </div>
        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6" data-testid="grant-deadline">
          <div className="label-eyebrow mb-2">Deadline</div>
          <div className="font-heading text-2xl font-medium text-[#1A1A1A]">
            {deadlineDate ? deadlineDate.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "Open"}
          </div>
          {days != null && days > 0 && <div className="text-xs text-[#C1654B] mt-1">{days} days left</div>}
        </div>
        <div className="bg-white border border-[#E6E4DF] rounded-lg p-6">
          <div className="label-eyebrow mb-2">Status</div>
          <div className="font-heading text-2xl font-medium text-[#1A1A1A] capitalize">{grant.status}</div>
        </div>
      </div>

      <div className="bg-white border border-[#E6E4DF] rounded-lg p-8">
        <h2 className="font-heading text-2xl font-medium text-[#1A1A1A] mb-4">About this grant</h2>
        <p className="text-[#1A1A1A] leading-relaxed whitespace-pre-wrap" data-testid="grant-description">
          {grant.description || "No description provided."}
        </p>
        {grant.eligibility?.length > 0 && eligibilityItems.length > 0 && (
          <div className="mt-8">
            <div className="label-eyebrow mb-2">Eligibility</div>
            <ul className="list-disc pl-5 text-[#1A1A1A] space-y-1">
              {eligibilityItems.map((e, i) => <li key={`elig-${e.slice(0,20)}-${i}`}>{e}</li>)}
            </ul>
          </div>
        )}
      </div>

      <AIFitAnalysis grantId={grant.id} />
    </div>
  );
}
